/**
 * 放送コンテンツ編集 UI の純関数ロジック。
 *
 * 分割前は `components/BroadcastContentsEditor.tsx` にコンポーネントと同居していた
 * 純関数を集約した (`react-refresh/only-export-components` 対策も兼ねる)。
 * ロジックは一切変更しておらず、そのまま移設したのみ。
 *
 * ドメイン (データモデル) 寄りのデフォルト値・バリデーションは
 * `utils/broadcastContents.ts` 側に既にあるため、こちらでは重複実装せず import して使う。
 */
import type {
  BroadcastCondition,
  ConnectingLineDelayCondition,
  DelayBroadcastGranularity,
  DelayBroadcastPattern,
  FreeTextInputMode,
  FreeTextProperties,
} from '../../api/types';

import { GRANULARITY_DEFAULT_SSML, resolveFreeTextInputMode } from '../../utils/broadcastContents';
import { PLAYBACK_RATE_MAX, PLAYBACK_RATE_MIN } from './constants';

/**
 * 数値が有限で範囲内かどうかを判定する簡易バリデーション。
 * `NaN` / `Infinity` / 範囲外はすべて false を返す。
 */
export function isValidPlaybackRate(value: number): boolean {
  return Number.isFinite(value) && value >= PLAYBACK_RATE_MIN && value <= PLAYBACK_RATE_MAX;
}

/**
 * 自列車遅延パターンの新規行デフォルト値を生成する (「＋」追加時に使用)。
 *
 * Issue #527: `ssml_template` は **入れない (空欄 = 定型文マスタへ委譲)**。
 * 実値として焼き込むと個別文面がマスタより優先され、
 * 「定型文マスタを編集したら既存の空欄コンテンツの放送内容も一括で変わる」という
 * 受け入れ条件が成立しなくなる (詳細は `utils/broadcastContents.ts` の
 * `backfillDefaultSsmlTemplates` の説明を参照)。
 *
 * @param templates 後方互換のため引数は残す (現在は未使用)。未入力時に実際に
 *   読み上げられる文面は入力欄の placeholder として `DelayPatternRow` が表示する。
 */
export function createDefaultPattern(
  templates: Record<DelayBroadcastGranularity, string> = GRANULARITY_DEFAULT_SSML,
): DelayBroadcastPattern {
  void templates;
  return {
    delay_minutes_from: 0,
    granularity: '1分単位',
  };
}

/**
 * 放送粒度を切り替えたときの `ssml_template` を決める (Issue #527)。
 *
 * 「空欄 = 定型文マスタへ委譲」規約では、粒度別デフォルト文の選択は
 * **放送生成側の template_id 選択** (`self_train_delay_{粒度}` など) が担う。
 * したがって画面側は「未編集ならそのまま空欄に保つ」だけでよい。
 *
 * - 現在値が空欄、または **いずれかの粒度のデフォルト文と完全一致** (= ユーザー未編集)
 *   なら `undefined` (空欄 = マスタ委譲) を返す。
 * - ユーザーがカスタム編集した文面はそのまま維持する
 *   (勝手に消すと入力内容が失われるため)。
 *
 * @param current 現在の `ssml_template`
 * @param templates 対象カテゴリの粒度別デフォルト文セット (未編集判定に使う)。
 *   複数渡せるのは part2 の方面部分文が **判定モードで 2 セットある** ため
 *   (方面あり / whole_line 用の方面なし)。旧仕様ではモード切替のたびに
 *   どちらかが焼き込まれていたので、両方を「未編集」と判定する必要がある。
 */
export function resolveSsmlTemplateOnGranularityChange(
  current: string | undefined,
  ...templates: readonly Record<DelayBroadcastGranularity, string>[]
): string | undefined {
  if (typeof current !== 'string' || current.trim() === '') return undefined;
  const defaults = templates.flatMap((set) => Object.values(set));
  return defaults.includes(current) ? undefined : current;
}

/**
 * 既存の properties から入力モードを推測する。
 * - `input_mode` が明示されていればそれを使う。
 * - `audio_key` があれば audio、中身のある `ssml` があれば ssml、それ以外は text。
 *
 * 「通常テキストをデフォルトにする」仕様変更 (Issue #340) にあわせ、
 * 中身のない SSML (旧初期値の `<speak></speak>` など) は「SSML 未設定」として扱い、
 * 通常テキストモードにフォールバックする。これにより旧仕様で作成された
 * レコードを開いたときにも、意図せず SSML モードで開くことがなくなる。
 *
 * ⚠️ 実体は `utils/broadcastContents.ts` の {@link resolveFreeTextInputMode} に
 * 一本化した (Issue #545)。バリデーションが「画面に出ている入力欄だけ」を必須に
 * するために同じ判定を使う必要があり、両所に同じ実装を持つと片方だけ直したときに
 * 「見えない項目のせいで保存できない」状態が生まれるため。
 * 本関数は既存の呼び出し元 / テスト互換のための薄い再エクスポートである。
 */
export function resolveInputMode(props: Partial<FreeTextProperties>): FreeTextInputMode {
  return resolveFreeTextInputMode(props);
}

/**
 * `conditions[index]` に `patch` をマージした新配列を返す (immutable)。
 *
 * `BroadcastCondition` は `condition_type` を discriminant とする判別可能ユニオン
 * (接続線遅延 / 停車番線) になったため、`Partial<BroadcastCondition>` を受けると
 * 「どちらのメンバの部分集合か」が失われ、マージ結果の型が壊れてしまう。
 * そこで条件の具体型 `T` でジェネリック化し、**同一メンバ内の部分更新** だけを
 * 許可する (種別をまたぐ差し替えは patch ではなく `onReplace` 相当で行う)。
 */
export function patchCondition<T extends BroadcastCondition>(
  conditions: T[],
  index: number,
  patch: Partial<T>,
): T[] {
  return conditions.map((c, i) => (i === index ? { ...c, ...patch } : c));
}

/**
 * 種別混在の条件配列に対し、`index` 番目が **接続線遅延条件のときだけ** patch を適用する。
 *
 * 呼び出し側 (`BroadcastConditionsEditor`) は以前
 * `patchCondition(conditions as ConnectingLineDelayCondition[], index, patch)
 *  as BroadcastCondition[]` という二重キャストで patch していたが、これは
 * 「停車番線条件が混ざった配列」を接続線遅延条件の配列に偽装するもので、
 * 種別を取り違えた patch を型で防げなかった。
 *
 * 本関数は対象 1 件を `condition_type` で絞り込んでから {@link patchCondition} を
 * 適用するため、キャストが不要になる。対象が停車番線条件だった場合は
 * (= 呼び出し経路の誤り) 何もせず元配列を返す。停車番線条件の更新は種別ごとの
 * 完成済みオブジェクトを渡す `onReplace` 経路で行う。
 */
export function patchDelayConditionAt(
  conditions: BroadcastCondition[],
  index: number,
  patch: Partial<ConnectingLineDelayCondition>,
): BroadcastCondition[] {
  const target = conditions[index];
  if (!target || target.condition_type === 'arrival_track') return conditions;
  const patched = patchCondition([target], 0, patch)[0];
  if (!patched) return conditions;
  return conditions.map((c, i) => (i === index ? patched : c));
}

/**
 * 配列から `index` 番目を除去した新配列を返す。
 */
export function removeAt<T>(arr: T[], index: number): T[] {
  return arr.filter((_, i) => i !== index);
}
